/* globals window, CSS, requestAnimationFrame */

import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useMetaSelector } from '../meta/reducer';
import { organisation as organisationApi, stroot } from '@birdi/js-sdk';
import type { WorkspaceGeneralInfo } from '@birdi/js-sdk/src/public/organisation';

const initialWidgets = [
  {
    id: 'teamMembers',
    label: 'Team members',
    value: 1,
  },
  {
    id: 'guestCollaborators',
    label: 'Guest collaborators',
    value: 0,
  },
  {
    id: 'activeWorkspaces',
    label: 'Active workspaces',
    value: 1,
  },
  {
    id: 'mapsCreated',
    label: 'Maps created',
    value: 0,
  },
  {
    id: 'filesUploaded',
    label: 'Files uploaded',
    value: 0,
  },
  {
    id: 'storageUsed',
    label: 'Storage used',
    value: '0 GB',
  },
];

function dataToWidgets(data: WorkspaceGeneralInfo) {
  return [
    { id: 'teamMembers', label: 'Team members', value: data.teamMembers },
    { id: 'guestCollaborators', label: 'Guest collaborators', value: data.guestCollaborators },
    { id: 'activeWorkspaces', label: 'Active workspaces', value: data.activeWorkspaces },
    { id: 'mapsCreated', label: 'Maps created', value: data.mapsCreated },
    { id: 'filesUploaded', label: 'Files uploaded', value: data.filesUploaded },
    {
      id: 'storageUsed',
      label: 'Storage used',
      value: `${parseFloat(data.storageUsed.toFixed(2))} GB`,
    },
  ];
}

export interface GeneralWorkspaceInfoProps {
  /** When provided, use this data and do not fetch. null = parent is loading. */
  data?: WorkspaceGeneralInfo | null;
  /** When data is provided, use this for loading state. */
  isLoading?: boolean;
}

export const GeneralWorkspaceInfo: React.FC<GeneralWorkspaceInfoProps> = ({
  data: dataProp,
  isLoading: isLoadingProp,
}) => {
  const meta = useMetaSelector();
  const theme = useTheme();
  const [widgets, setWidgets] = useState(initialWidgets);
  const [isLoading, setIsLoading] = useState(dataProp === undefined);

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  useEffect(() => {
    if (dataProp !== undefined) {
      if (dataProp) setWidgets(dataToWidgets(dataProp));
      return;
    }
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const res = await organisationApi.getWorkspaceGeneralInfo(stroot('weeh5y'));

        if (res.body) {
          setWidgets(dataToWidgets(res.body));
        }
      } catch (error) {
        console.error('Error fetching dashboard widgets:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [workspaceId, dataProp]);

  const loading = dataProp !== undefined ? Boolean(isLoadingProp) : isLoading;
  const displayWidgets = dataProp ? dataToWidgets(dataProp) : widgets;

  return (
    <div
      css={css`
        display: grid;
        padding: 0 0.25em;
        grid-template-columns: repeat(6, 1fr);
        gap: 0.5em;
        margin-bottom: 0.5em;

        @media (max-width: ${breakpoints.s}) {
          grid-template-columns: repeat(3, 1fr);
          grid-template-rows: repeat(2, 1fr);
        }
      `}
    >
      {displayWidgets.map((widget) => (
        <div
          key={widget.id}
          css={css`
            background: ${theme.mono0};
            border-radius: 1rem;
            padding: 1em 0.25em;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            &:hover {
              box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
            }
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              gap: 0.25rem;
              color: ${theme.midnight};
              text-align: center;
            `}
          >
            <div
              css={css`
                font-size: 1.15rem;
                font-weight: 550;
              `}
            >
              {widget.value}
            </div>
            <div
              css={css`
                font-size: 0.9rem;
              `}
            >
              {loading ? '...' : widget.label}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
